import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, post, put, toastError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_SKY, isKnownQuality } from "./rigSky";
import { DEFAULT_FLOOR_HEIGHT, DEFAULT_GRID, bomSignature, conflictIds, designHealth, localSummary, nextLabel, rotated, snapPosition, footprint, suggestPlacement, r3, bounds as boundsOf } from "./rigGeometry";
import { worldToLatLng } from "./siteGeo";

const MAX_HISTORY = 120;
const AUTOSAVE_MS = 1400;
const ENV_KEY = "rig.env.v1";
/** Quality «normal» is the default everywhere; «hoy» (post-processing) is opt-in. Older stored values (auto/standard) are migrated. */
export const DEFAULT_ENV = { ground: "asfalt", fence: true, scale: true, joints: true, props: true, sky: { ...DEFAULT_SKY }, quality: "normal" };
const newId = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/**
 * Editor state for one project's rig design: working copy + undo/redo, selection, view state, autosave and the
 * server-side estimate. Every mutation goes through `commit` so it is undoable and (debounced) persisted.
 */
export function useRigEditor(projectId) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canEdit = can("projects:write");
  const key = ["rig-design", projectId];

  const designQ = useQuery({ queryKey: key, queryFn: () => get(`/projects/${projectId}/rig-design`), staleTime: 60_000, refetchOnWindowFocus: false });
  const libraryQ = useQuery({ queryKey: ["rig-library"], queryFn: () => get("/rig/library"), staleTime: 5 * 60_000 });
  const siteConfigQ = useQuery({ queryKey: ["site-config"], queryFn: () => get("/site/config"), staleTime: 30 * 60_000, retry: 1 });

  const [doc, setDoc] = useState({ modules: [], past: [], future: [] });
  const modules = doc.modules;
  const [floors, setFloorsState] = useState(1);
  const [name, setName] = useState("Riggdesign");
  const [site, setSiteState] = useState(null); // Tomt-modus: {enabled, lat, lng, bearing_deg, anchor_x, anchor_z, layer, zoom, address}
  const [version, setVersion] = useState(0);
  const [loadedId, setLoadedId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [selection, setSelection] = useState(() => new Set());
  const [activeFloor, setActiveFloor] = useState("all"); // 0..n-1 or "all"
  const [view, setView] = useState("3D"); // "3D" | "2D"
  const [mode, setMode] = useState("design"); // "design" | "delivery"
  const [showLabels, setShowLabels] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(true);
  // Omgivelser (site dressing) – a view preference, remembered per browser
  const [env, setEnvState] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(ENV_KEY) || "null") || {};
      const quality = isKnownQuality(stored.quality) ? stored.quality : DEFAULT_ENV.quality;
      return { ...DEFAULT_ENV, ...stored, quality, sky: { ...DEFAULT_ENV.sky, ...(stored.sky || {}) } };
    } catch { return DEFAULT_ENV; }
  });
  const setEnv = useCallback((patch) => setEnvState((e) => {
    const p = typeof patch === "function" ? patch(e) : patch;
    const next = { ...e, ...p, sky: { ...e.sky, ...(p.sky || {}) } };
    try { localStorage.setItem(ENV_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    return next;
  }), []);
  const [fit, setFit] = useState({ key: 0, preset: "iso", ids: null });
  /** fitView(preset?, ids?) – frame the whole rig or a set of module ids. */
  const fitView = useCallback((preset, ids) => setFit((f) => ({ key: f.key + 1, preset: typeof preset === "string" ? preset : f.preset, ids: Array.isArray(ids) && ids.length ? ids : null })), []);
  const [revealKey, setRevealKey] = useState(0); // bumps after a generated layout arrives → staged reveal in the scene
  const [delivery, setDelivery] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [snapGuides, setSnapGuides] = useState(null); // live snapping feedback from the scene
  const [lastGeneration, setLastGeneration] = useState(null);
  const skipAutosave = useRef(false);

  // hydrate the working copy from the server once per design version we have not seen
  useEffect(() => {
    const d = designQ.data?.design;
    if (!d) return;
    const sig = `${d.id}:${d.version}`;
    if (sig === loadedId) return;
    if (dirty) return; // never clobber local edits with a background refetch
    setDoc({ modules: d.modules || [], past: [], future: [] });
    setFloorsState(Math.max(1, d.floors || 1));
    setName(d.name || "Riggdesign");
    setSiteState(d.site || null);
    setVersion(d.version || 0);
    setLoadedId(sig);
    setDelivery(designQ.data.delivery || null);
    setSavedAt(d.updated_at || null);
    setFit((f) => ({ ...f, key: f.key + 1, ids: null }));
  }, [designQ.data]);

  const types = useMemo(() => {
    const map = {};
    for (const t of designQ.data?.types || []) map[t.id] = t;
    for (const t of libraryQ.data?.items || []) map[t.id] = t;
    return map;
  }, [designQ.data, libraryQ.data]);
  const floorHeight = designQ.data?.design?.floor_height || DEFAULT_FLOOR_HEIGHT;
  const grid = designQ.data?.design?.grid || DEFAULT_GRID;
  const project = designQ.data?.project || null;

  const summary = useMemo(() => localSummary(modules, types, project?.capacity_persons || 0), [modules, types, project]);
  const conflicts = useMemo(() => conflictIds(modules, types), [modules, types]);
  const health = useMemo(() => designHealth(modules, types, { floors, capacityTarget: project?.capacity_persons || 0, conflicts }), [modules, types, floors, project, conflicts]);
  const deliveryById = useMemo(() => {
    const m = {};
    for (const it of delivery?.items || []) m[it.id] = it;
    return m;
  }, [delivery]);

  // estimate through the calculation engine – only re-fetched when the BOM changes
  const sig = bomSignature(modules);
  const estimateQ = useQuery({
    queryKey: ["rig-estimate", projectId, sig],
    queryFn: () => post(`/projects/${projectId}/rig-design/summary`, { floors, modules: modules.map(({ id, module_type_id, floor, x, z, rot }) => ({ id, module_type_id, floor, x, z, rot })) }),
    enabled: !!designQ.data && modules.length > 0,
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });

  // ---- history ----
  const commit = useCallback((updater, opts = {}) => {
    setDoc((d) => {
      const next = typeof updater === "function" ? updater(d.modules) : updater;
      if (next === d.modules) return d;
      return opts.silent ? { ...d, modules: next } : { modules: next, past: [...d.past.slice(-MAX_HISTORY + 1), d.modules], future: [] };
    });
    setDirty(true);
  }, []);

  const undo = useCallback(() => {
    setDoc((d) => (d.past.length ? { modules: d.past[d.past.length - 1], past: d.past.slice(0, -1), future: [d.modules, ...d.future] } : d));
    setDirty(true);
  }, []);
  const redo = useCallback(() => {
    setDoc((d) => (d.future.length ? { modules: d.future[0], past: [...d.past, d.modules], future: d.future.slice(1) } : d));
    setDirty(true);
  }, []);

  // ---- selection ----
  const select = useCallback((id, { toggle = false } = {}) => {
    setSelection((s) => {
      if (!id) return new Set();
      if (toggle) { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }
      if (s.size === 1 && s.has(id)) return s;
      return new Set([id]);
    });
  }, []);
  const clearSelection = useCallback(() => setSelection(new Set()), []);
  const selectedIds = useMemo(() => [...selection], [selection]);
  const selected = useMemo(() => modules.filter((m) => selection.has(m.id)), [modules, selection]);
  const primary = selected[0] || null;

  // ---- mutations on the working copy ----
  const addModule = useCallback((typeId, pos = {}) => {
    const t = types[typeId];
    if (!t) return null;
    const floor = pos.floor ?? (activeFloor === "all" ? 0 : activeFloor);
    const rot = pos.rot ?? 0;
    let placement;
    const id = newId();
    commit((prev) => {
      const others = prev;
      const base = pos.x != null && pos.z != null ? { x: pos.x, z: pos.z } : suggestPlacement(prev, types, t, floor, rot);
      const snapped = pos.snap === false ? { x: r3(base.x), z: r3(base.z) } : snapPosition(base.x, base.z, footprint(t, rot), { id, module_type_id: typeId, floor }, others, types, grid, snapEnabled ? undefined : 0);
      placement = { id, module_type_id: typeId, floor, x: snapped.x, z: snapped.z, rot, asset_id: null, label: nextLabel(prev, t), delivery_status: null, meta: {} };
      return [...prev, placement];
    });
    setSelection(new Set([id]));
    return id;
  }, [types, activeFloor, grid, commit, snapEnabled]);

  const removeSelected = useCallback(() => {
    if (!selection.size) return;
    commit((prev) => prev.filter((m) => !selection.has(m.id)));
    setSelection(new Set());
  }, [selection, commit]);

  const rotateSelected = useCallback((delta = 90) => {
    if (!selection.size) return;
    commit((prev) => prev.map((m) => (selection.has(m.id) && types[m.module_type_id] ? rotated(m, types[m.module_type_id], delta) : m)));
  }, [selection, types, commit]);

  const duplicateSelected = useCallback(() => {
    if (!selection.size) return;
    const ids = [];
    commit((prev) => {
      const copies = [];
      const sel = prev.filter((m) => selection.has(m.id));
      for (const m of sel) {
        const t = types[m.module_type_id];
        if (!t) continue;
        const fp = footprint(t, m.rot);
        const id = newId();
        ids.push(id);
        copies.push({ ...m, id, x: r3(m.x + fp.sx), asset_id: null, delivery_status: null, label: nextLabel([...prev, ...copies], t) });
      }
      return [...prev, ...copies];
    });
    if (ids.length) setSelection(new Set(ids));
  }, [selection, types, commit]);

  const moveSelectedBy = useCallback((dx, dz) => {
    if (!selection.size) return;
    commit((prev) => prev.map((m) => (selection.has(m.id) ? { ...m, x: r3(m.x + dx), z: r3(m.z + dz) } : m)));
  }, [selection, commit]);

  const updateModule = useCallback((id, patch, { silent = false } = {}) => {
    commit((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)), { silent });
  }, [commit]);

  const setFloorOfSelected = useCallback((floor) => {
    if (!selection.size) return;
    commit((prev) => prev.map((m) => (selection.has(m.id) ? { ...m, floor } : m)));
  }, [selection, commit]);

  const setFloors = useCallback((n) => {
    const f = Math.max(1, Math.min(4, Number(n) || 1));
    setFloorsState(f);
    commit((prev) => prev.map((m) => (m.floor >= f ? { ...m, floor: f - 1 } : m)));
    if (activeFloor !== "all" && activeFloor >= f) setActiveFloor(f - 1);
  }, [commit, activeFloor]);

  // drag: live updates are silent (no history spam); `beginDrag` records one history entry
  const dragSnapshot = useRef(null);
  const beginDrag = useCallback(() => { dragSnapshot.current = modules; }, [modules]);
  const dragTo = useCallback((positions) => {
    // positions: {id: {x, z}} – live, silent
    setDoc((d) => ({ ...d, modules: d.modules.map((m) => (positions[m.id] ? { ...m, x: positions[m.id].x, z: positions[m.id].z } : m)) }));
  }, []);
  const endDrag = useCallback((moved) => {
    const snap = dragSnapshot.current;
    dragSnapshot.current = null;
    if (!snap) return;
    if (moved) {
      setDoc((d) => ({ ...d, past: [...d.past.slice(-MAX_HISTORY + 1), snap], future: [] }));
      setDirty(true);
    }
  }, []);

  // ---- persistence ----
  const saveM = useMutation({
    mutationFn: (payload) => put(`/projects/${projectId}/rig-design`, payload),
    onSuccess: (res) => {
      setVersion(res.design.version);
      setLoadedId(`${res.design.id}:${res.design.version}`);
      setSavedAt(res.design.updated_at);
      setDelivery(res.delivery || null);
      setDirty(false);
      qc.setQueryData(key, res);
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
    onError: (e) => {
      if (e?.response?.status === 409) {
        toast.error(e.response.data?.detail || "Designet er endret av en annen bruker", { action: { label: "Last inn på nytt", onClick: () => { setDirty(false); setLoadedId(null); qc.invalidateQueries({ queryKey: key }); } } });
      } else toastError(e, "Kunne ikke lagre riggdesignet");
    },
  });
  const save = useCallback(() => {
    if (!canEdit || saveM.isPending) return;
    saveM.mutate({ name, floors, floor_height: floorHeight, grid, version, site, modules: modules.map(({ id, module_type_id, floor, x, z, rot, asset_id, label, delivery_status, meta }) => ({ id, module_type_id, floor, x, z, rot, asset_id, label, delivery_status, meta })) });
  }, [canEdit, saveM, name, floors, floorHeight, grid, version, site, modules]);

  useEffect(() => {
    if (!dirty || !canEdit || dragSnapshot.current) return;
    if (skipAutosave.current) { skipAutosave.current = false; return; }
    const t = setTimeout(() => save(), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [dirty, modules, floors, name, site, canEdit, save]);

  // ---- Tomt-modus (real-world placement) ----
  const siteConfig = siteConfigQ.data || null;
  const [siteTool, setSiteToolState] = useState(null); // null | "move" – the site is being slid under the rig
  const setSiteTool = useCallback((t) => setSiteToolState((cur) => (typeof t === "function" ? t(cur) : t)), []);
  const setSite = useCallback((patch) => {
    setSiteState((s) => {
      if (!s) return s; // nothing to patch until a coordinate exists
      const p = typeof patch === "function" ? patch(s) : patch;
      const next = { ...s, ...p };
      if (next.bearing_deg != null) next.bearing_deg = r3(((Number(next.bearing_deg) % 360) + 360) % 360);
      return next;
    });
    setDirty(true);
  }, []);
  /**
   * Turn the site view on/off. First activation seeds the placement from the project's coordinates under the rig
   * centre. Returns false when there is nothing to anchor to – the caller then asks for an address.
   */
  const enableSite = useCallback((on) => {
    if (!on) {
      setSiteState((s) => (s ? { ...s, enabled: false } : s));
      setSiteToolState(null);
      setDirty(true);
      return true;
    }
    if (site && site.lat != null) {
      if (!site.enabled) { setSiteState({ ...site, enabled: true }); setDirty(true); }
      return true;
    }
    const lat = project?.lat, lng = project?.lng;
    if (lat == null || lng == null) return false;
    const b = boundsOf(modules, types);
    setSiteState({ enabled: true, lat, lng, bearing_deg: 0, anchor_x: r3(b.cx), anchor_z: r3(b.cz), layer: siteConfig?.default_layer || "satellite", zoom: siteConfig?.default_zoom || 18, address: project?.address || null });
    setDirty(true);
    return true;
  }, [site, project, modules, types, siteConfig]);
  /** Set a new coordinate for the site (address search / project coordinates) and put it under the rig centre. */
  const placeSiteAt = useCallback((lat, lng, address) => {
    const b = boundsOf(modules, types);
    setSiteState((s) => ({ bearing_deg: 0, layer: siteConfig?.default_layer || "satellite", zoom: siteConfig?.default_zoom || 18, ...(s || {}), enabled: true, lat, lng, address: address ?? s?.address ?? null, anchor_x: r3(b.cx), anchor_z: r3(b.cz) }));
    setDirty(true);
  }, [modules, types, siteConfig]);
  const centerSiteUnderRig = useCallback(() => {
    const b = boundsOf(modules, types);
    setSite({ anchor_x: r3(b.cx), anchor_z: r3(b.cz) });
  }, [modules, types, setSite]);
  /** Forget the placement entirely (explicit `site: null` on save clears it server-side). */
  const clearSite = useCallback(() => { setSiteState(null); setSiteToolState(null); setDirty(true); }, []);
  const siteEnabled = !!(site && site.enabled && site.lat != null);
  useEffect(() => { if (!siteEnabled || mode !== "design") setSiteToolState(null); }, [siteEnabled, mode]);
  const siteLayer = useMemo(() => (siteConfig?.layers || []).find((l) => l.id === (site?.layer || siteConfig?.default_layer)) || null, [siteConfig, site?.layer]);
  const rigBounds = useMemo(() => boundsOf(modules, types), [modules, types]);
  const rigCenterLatLng = useMemo(() => (siteEnabled ? worldToLatLng(site, rigBounds.cx, rigBounds.cz) : null), [siteEnabled, site, rigBounds]);

  const generateM = useMutation({
    mutationFn: (params) => post(`/projects/${projectId}/rig-design/generate`, { ...params, save: true }),
    onSuccess: (res) => {
      setDoc((d) => ({ modules: res.design.modules || [], past: [...d.past.slice(-MAX_HISTORY + 1), d.modules], future: [] }));
      setFloorsState(res.design.floors || 1);
      setVersion(res.design.version);
      setLoadedId(`${res.design.id}:${res.design.version}`);
      setSavedAt(res.design.updated_at);
      setDelivery(res.delivery || null);
      setDirty(false);
      setSelection(new Set());
      setActiveFloor("all");
      setFit((f) => ({ ...f, key: f.key + 1, ids: null }));
      setRevealKey((k) => k + 1);
      qc.setQueryData(key, res);
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      setLastGeneration({ kpi: res.summary.kpi, generator: res.generator, floors: res.design.floors, at: Date.now() });
    },
    onError: (e) => toastError(e, "Kunne ikke generere riggforslag"),
  });

  const applyM = useMutation({
    mutationFn: (body = {}) => post(`/projects/${projectId}/rig-design/apply-to-calculation`, body),
    onSuccess: (res) => {
      const c = res.calculation;
      toast.success(res.created ? `Kalkyle ${c.calc_number} opprettet fra riggdesignet${res.superseded ? ` (erstatter ${res.superseded.calc_number})` : ""}` : `Kalkyle ${c.calc_number} oppdatert fra riggdesignet`, { description: `${res.line_count} linjer · planlagt oppsett oppdatert` });
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["calculation"] });
    },
    onError: (e) => toastError(e, "Kunne ikke overføre til kalkyle"),
  });

  const linkM = useMutation({
    mutationFn: () => post(`/projects/${projectId}/rig-design/link-assets`),
    onSuccess: (res) => {
      setDoc((d) => ({ ...d, modules: res.design.modules || [] }));
      setVersion(res.design.version);
      setLoadedId(`${res.design.id}:${res.design.version}`);
      setDelivery(res.delivery || null);
      qc.setQueryData(key, res);
      toast.success(res.linked ? `${res.linked} fysiske moduler koblet til designet` : "Ingen ledige allokerte moduler å koble");
    },
    onError: (e) => toastError(e, "Kunne ikke koble moduler"),
  });


  const focusModules = useCallback((ids) => {
    const list = (ids || []).filter(Boolean);
    setSelection(new Set(list));
    if (list.length) {
      const f = modules.find((m) => m.id === list[0])?.floor;
      if (f != null && activeFloor !== "all" && list.some((id) => modules.find((m) => m.id === id)?.floor !== activeFloor)) setActiveFloor("all");
      setFit((prev) => ({ key: prev.key + 1, preset: prev.preset, ids: list }));
    }
  }, [modules, activeFloor]);

  return {
    // data
    loading: designQ.isLoading || libraryQ.isLoading, error: designQ.error || libraryQ.error, refetch: () => { designQ.refetch(); libraryQ.refetch(); },
    exists: designQ.data?.exists ?? false, project, calculation: designQ.data?.calculation || null, library: libraryQ.data || null, types, floorHeight, grid,
    modules, floors, name, setName: (n) => { setName(n); setDirty(true); }, version, savedAt, dirty, saving: saveM.isPending, canEdit,
    summary, estimate: estimateQ.data?.estimate || null, estimateLoading: estimateQ.isFetching, conflicts, health, serverSummary: designQ.data?.summary || null,
    snapGuides, setSnapGuides, lastGeneration, dismissGeneration: () => setLastGeneration(null), revealKey,
    delivery, deliveryById,
    // Tomt-modus
    site, siteConfig, siteEnabled, setSite, enableSite, placeSiteAt, centerSiteUnderRig, clearSite, siteTool, setSiteTool, siteLayer, rigBounds, rigCenterLatLng,
    // view state
    activeFloor, setActiveFloor, view, setView, mode, setMode, showLabels, setShowLabels, snapEnabled, setSnapEnabled, env, setEnv, fit, fitView, focusModules,
    // selection
    selection, selectedIds, selected, primary, select, clearSelection,
    // actions
    addModule, removeSelected, rotateSelected, duplicateSelected, moveSelectedBy, updateModule, setFloorOfSelected, setFloors, undo, redo, canUndo: doc.past.length > 0, canRedo: doc.future.length > 0,
    beginDrag, dragTo, endDrag, save, generate: generateM.mutate, generating: generateM.isPending, applyToCalculation: applyM.mutate, applying: applyM.isPending, linkAssets: linkM.mutate, linking: linkM.isPending,
    unlinkSelected: () => { if (!selection.size) return; commit((prev) => prev.map((m) => (selection.has(m.id) ? { ...m, asset_id: null, delivery_status: null } : m))); },
  };
}
